import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Save, Image as ImageIcon, Building2, Star, Receipt, CreditCard, Tag, Plus, X, ShieldAlert, Printer, FileText, StickyNote, Bell, MessageSquare, Cake } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";

const ALL_MODES = [
  { id: "ristorante", default: "Ristorante" }, { id: "festa", default: "Feste del paese" },
  { id: "negozio", default: "Negozio" }, { id: "lavanderia", default: "Lavanderia" },
  { id: "pizzeria", default: "Pizzeria d'asporto" }, { id: "bar", default: "Bar/Tavola calda" },
  { id: "gelateria", default: "Gelateria" }, { id: "panetteria", default: "Panetteria/Pasticceria" },
  { id: "tabacchi", default: "Tabacchi/Ricevitoria" }, { id: "edicola", default: "Edicola" },
  { id: "parrucchiere", default: "Parrucchiere/Estetista" }, { id: "alimentari", default: "Alimentari/Market" },
  { id: "macelleria", default: "Macelleria/Pescheria" }, { id: "enoteca", default: "Enoteca" },
  { id: "autolavaggio", default: "Autolavaggio" }, { id: "bnb", default: "B&B/Affittacamere" },
  { id: "alloggio_dedicato", default: "Alberghiero dedicato" },
  { id: "toelettatura", default: "Toelettatura & Negozio Animali" },
];

export default function Branding() {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({
    business_name: "", address: "", vat_number: "", fiscal_code: "",
    phone: "", email: "", receipt_footer: "",
    receipt_header: "", receipt_thank_you: "", receipt_promo: "",
    custom_payment_methods: [],
    mode_labels: {},
    loyalty_points_per_euro: 1,
    loyalty_redeem_threshold: 100,
    loyalty_redeem_value: 1,
    loyalty_auto_redeem: false,
    management_pin: "",
    comanda_config: { notes_bold: true, notes_uppercase: true, notes_size: "normal" },
    preconto_header: "",
    preconto_footer: "",
    line_note_suggestions: [],
    notification_config: {
      provider: "wa_deeplink",
      reminder_template: "Ciao {owner_name}! Ti ricordiamo l'appuntamento di {pet_name} il {date} alle {time} presso {business_name}. A presto!",
      hours_before: 48,
      auto_send: false,
      meta_access_token: "",
      meta_phone_number_id: "",
      twilio_sid: "",
      twilio_token: "",
      twilio_from: "",
      birthday_default_enabled: false,
      birthday_discount_pct: 10,
      birthday_message_template: "🎂 Buon compleanno {pet_name}! Per festeggiare, {owner_name}, abbiamo uno sconto del {discount_pct}% per te per tutto il mese. Ti aspettiamo da {business_name}!",
    },
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/tenant/me").then(r => {
      setTenant(r.data);
      setForm({
        business_name: r.data.business_name || "",
        address: r.data.address || "",
        vat_number: r.data.vat_number || "",
        fiscal_code: r.data.fiscal_code || "",
        phone: r.data.phone || "",
        email: r.data.email || "",
        receipt_footer: r.data.receipt_footer || "",
        receipt_header: r.data.receipt_header || "",
        receipt_thank_you: r.data.receipt_thank_you || "",
        receipt_promo: r.data.receipt_promo || "",
        custom_payment_methods: r.data.custom_payment_methods || [],
        mode_labels: r.data.mode_labels || {},
        loyalty_points_per_euro: r.data.loyalty_points_per_euro ?? 1,
        loyalty_redeem_threshold: r.data.loyalty_redeem_threshold ?? 100,
        loyalty_redeem_value: r.data.loyalty_redeem_value ?? 1,
        loyalty_auto_redeem: r.data.loyalty_auto_redeem ?? false,
        management_pin: r.data.management_pin || "",
        comanda_config: r.data.comanda_config || { notes_bold: true, notes_uppercase: true, notes_size: "normal" },
        preconto_header: r.data.preconto_header || "",
        preconto_footer: r.data.preconto_footer || "",
        line_note_suggestions: r.data.line_note_suggestions || [],
        notification_config: {
          provider: r.data.notification_config?.provider || "wa_deeplink",
          reminder_template: r.data.notification_config?.reminder_template || "Ciao {owner_name}! Ti ricordiamo l'appuntamento di {pet_name} il {date} alle {time} presso {business_name}. A presto!",
          hours_before: r.data.notification_config?.hours_before || 48,
          auto_send: r.data.notification_config?.auto_send || false,
          meta_access_token: r.data.notification_config?.meta_access_token || "",
          meta_phone_number_id: r.data.notification_config?.meta_phone_number_id || "",
          twilio_sid: r.data.notification_config?.twilio_sid || "",
          twilio_token: r.data.notification_config?.twilio_token || "",
          twilio_from: r.data.notification_config?.twilio_from || "",
          birthday_default_enabled: r.data.notification_config?.birthday_default_enabled || false,
          birthday_discount_pct: r.data.notification_config?.birthday_discount_pct ?? 10,
          birthday_message_template: r.data.notification_config?.birthday_message_template || "🎂 Buon compleanno {pet_name}! Per festeggiare, {owner_name}, abbiamo uno sconto del {discount_pct}% per te per tutto il mese. Ti aspettiamo da {business_name}!",
        },
      });
      setLogoPreview(r.data.logo_data_url || null);
    });
  }, []);

  const addPaymentMethod = () => {
    const label = prompt("Nome del metodo di pagamento (es. Sumup, Postepay, Buoni Pasto):");
    if (!label || !label.trim()) return;
    const id = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 24);
    setForm((f) => ({ ...f, custom_payment_methods: [...(f.custom_payment_methods || []), { id, label: label.trim() }] }));
  };
  const removePaymentMethod = (id) => {
    setForm((f) => ({ ...f, custom_payment_methods: (Array.isArray(f.custom_payment_methods) ? f.custom_payment_methods : []).filter((m) => m.id !== id) }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return toast.error("Seleziona un'immagine (PNG, JPG, SVG)");
    }
    if (file.size > 500_000) {
      return toast.error("Logo troppo grande. Max 500KB.");
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target.result);
      toast.success("Logo caricato - ricorda di salvare!");
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    if (!window.confirm("Rimuovere il logo dallo scontrino?")) return;
    try {
      await api.delete("/tenant/branding/logo");
      setLogoPreview(null);
      const r = await api.get("/tenant/me");
      setTenant(r.data);
      toast.success("Logo rimosso");
    } catch { toast.error("Errore"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (logoPreview !== tenant?.logo_data_url) {
        payload.logo_data_url = logoPreview || "";
      }
      const r = await api.patch("/tenant/branding", payload);
      setTenant(r.data);
      toast.success("Branding salvato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  // tenant for preview (merge live form changes)
  const previewTenant = {
    ...tenant,
    ...form,
    logo_data_url: logoPreview,
  };

  // demo order for preview
  const demoOrder = {
    receipt_number: 42,
    closed_at: new Date().toISOString(),
    lines: [
      { name: "Caffè espresso", qty: 2, price: 1.20, vat: 22 },
      { name: "Cornetto vuoto", qty: 3, price: 1.50, vat: 10 },
    ],
    total: 6.90,
    vat_break: { "10.0": 4.50, "22.0": 2.40 },
    payment_method: "card",
  };

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Branding</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Personalizza scontrino</h1>
        <p className="text-velta-mute">Logo aziendale, dati fiscali, messaggio promozionale: tutto stampato in cima e in fondo ad ogni scontrino emesso.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* COL 1: form */}
        <div className="space-y-5">
          {/* Logo uploader */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="w-4 h-4 text-velta-mute" />
              <h2 className="font-heading font-semibold">Logo aziendale</h2>
            </div>
            {logoPreview ? (
              <div className="flex items-center gap-4">
                <div className="bg-velta-bg border border-velta-border rounded-md p-3 w-32 h-32 flex items-center justify-center">
                  <img src={logoPreview} alt="logo preview" className="max-w-full max-h-full object-contain" data-testid="brand-logo-preview" />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    data-testid="brand-logo-replace"
                    onClick={() => fileRef.current?.click()}
                    className="h-10 px-4 border border-velta-border rounded-md text-sm font-medium hover:border-velta-ink inline-flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" /> Sostituisci
                  </button>
                  <button
                    data-testid="brand-logo-delete"
                    onClick={removeLogo}
                    className="h-10 px-4 border border-velta-error/30 text-velta-error rounded-md text-sm font-medium hover:bg-red-50 inline-flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Rimuovi
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="brand-logo-upload"
                onClick={() => fileRef.current?.click()}
                className="w-full h-32 border-2 border-dashed border-velta-border rounded-md hover:border-velta-ink hover:bg-velta-bg flex flex-col items-center justify-center gap-2 text-velta-mute"
              >
                <Upload className="w-6 h-6" />
                <span className="text-sm">Carica logo (PNG/JPG/SVG, max 500KB)</span>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid="brand-logo-file-input"
            />
            <p className="text-xs text-velta-mute mt-3">Consigliato: PNG con sfondo trasparente, formato quadrato o 2:1. Visualizzato a 64×64px sullo scontrino.</p>
          </div>

          {/* Dati fiscali */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-velta-mute" />
              <h2 className="font-heading font-semibold">Dati fiscali</h2>
            </div>
            <div className="space-y-3">
              <Field label="Ragione sociale" value={form.business_name} onChange={v => setForm({ ...form, business_name: v })} testid="brand-business-name" />
              <Field label="Indirizzo" value={form.address} onChange={v => setForm({ ...form, address: v })} testid="brand-address" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Partita IVA" value={form.vat_number} onChange={v => setForm({ ...form, vat_number: v })} testid="brand-vat" />
                <Field label="Codice fiscale" value={form.fiscal_code} onChange={v => setForm({ ...form, fiscal_code: v })} testid="brand-cf" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Telefono" value={form.phone} onChange={v => setForm({ ...form, phone: v })} testid="brand-phone" />
                <Field label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="brand-email" />
              </div>
            </div>
          </div>

          {/* Diciture scontrino */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-[#1E2D5B]" />
              <h2 className="font-heading font-semibold">Diciture scontrino</h2>
            </div>
            <div className="space-y-3">
              <Field label="Intestazione (sopra dati fiscali)" value={form.receipt_header} onChange={v => setForm({ ...form, receipt_header: v })} testid="brand-rcpt-header" placeholder="Es: ★ Bar Da Mario dal 1965 ★" />
              <Field label="Banner promo" value={form.receipt_promo} onChange={v => setForm({ ...form, receipt_promo: v })} testid="brand-rcpt-promo" placeholder="Es: 'Punti fedeltà 10% in più questo weekend!'" />
              <Field label="Ringraziamento" value={form.receipt_thank_you} onChange={v => setForm({ ...form, receipt_thank_you: v })} testid="brand-rcpt-ty" placeholder="Es: Grazie e arrivederci! ☕" />
              <Field label="Footer (in fondo)" value={form.receipt_footer} onChange={v => setForm({ ...form, receipt_footer: v })} testid="brand-rcpt-footer" placeholder="Es: 'Seguici su Instagram @barmariosrl'" />
            </div>
          </div>

          {/* Metodi pagamento custom */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-velta-mute" />
                <h2 className="font-heading font-semibold">Metodi di pagamento extra</h2>
              </div>
              <button data-testid="brand-add-payment" type="button" onClick={addPaymentMethod} className="text-xs font-medium px-3 h-8 bg-velta-ink text-white rounded-md inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Aggiungi
              </button>
            </div>
            <p className="text-xs text-velta-mute mb-3">
              Contanti, Carta, Ticket e Misto sono sempre attivi. Qui aggiungi i tuoi extra (es. Sumup, Postepay, Buoni Pasto).
            </p>
            <div className="space-y-2">
              {(form.custom_payment_methods || []).length === 0 && (
                <div className="text-xs text-velta-mute italic py-2">Nessun metodo extra configurato.</div>
              )}
              {(Array.isArray(form.custom_payment_methods) ? form.custom_payment_methods : []).map((m) => (
                <div key={m.id} data-testid={`brand-payment-row-${m.id}`} className="flex items-center gap-2 bg-velta-bg rounded-md px-3 py-2">
                  <CreditCard className="w-4 h-4 text-velta-mute shrink-0" />
                  <span className="text-sm font-medium flex-1">{m.label}</span>
                  <span className="text-[10px] font-mono text-velta-mute">{m.id}</span>
                  <button onClick={() => removePaymentMethod(m.id)} className="text-velta-error"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Etichette modalità */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-4 h-4 text-velta-mute" />
              <h2 className="font-heading font-semibold">Etichette modalità POS</h2>
            </div>
            <p className="text-xs text-velta-mute mb-3">
              Personalizza il nome di ogni modalità come vuoi che appaia sul tablet (es. &quot;Bar&quot; → &quot;Caffetteria Da Mario&quot;). Lascia vuoto per usare il nome standard.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {(Array.isArray(ALL_MODES) ? ALL_MODES : []).filter(m => (Array.isArray(tenant?.modes) ? tenant.modes : []).includes(m.id)).map((m) => (
                <div key={m.id}>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-velta-mute mb-1">{m.default}</label>
                  <input
                    data-testid={`brand-mode-label-${m.id}`}
                    value={form.mode_labels?.[m.id] || ""}
                    onChange={(e) => setForm({ ...form, mode_labels: { ...form.mode_labels, [m.id]: e.target.value } })}
                    placeholder={m.default}
                    className="h-10 w-full px-3 border border-velta-border rounded-md text-sm focus:border-velta-ink focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Programma Fedeltà */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-[#2F9E77]" />
              <h2 className="font-heading font-semibold">Programma Fedeltà</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Punti accumulati per ogni €</label>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="brand-loy-rate"
                    type="number" step="0.5" min="0" max="100"
                    value={form.loyalty_points_per_euro}
                    onChange={(e) => setForm({ ...form, loyalty_points_per_euro: parseFloat(e.target.value) || 0 })}
                    className="h-10 w-24 px-3 border border-velta-border rounded-md text-sm font-mono focus:border-velta-ink focus:outline-none"
                  />
                  <span className="text-sm text-velta-mute">punti / € speso (default 1)</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Soglia riscatto</label>
                  <input
                    data-testid="brand-loy-threshold"
                    type="number" step="1" min="1"
                    value={form.loyalty_redeem_threshold}
                    onChange={(e) => setForm({ ...form, loyalty_redeem_threshold: parseInt(e.target.value) || 1 })}
                    className="h-10 w-full px-3 border border-velta-border rounded-md text-sm font-mono focus:border-velta-ink focus:outline-none"
                  />
                  <div className="text-[11px] text-velta-mute mt-1">punti necessari per attivare uno sconto</div>
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Valore sconto (€)</label>
                  <input
                    data-testid="brand-loy-value"
                    type="number" step="0.5" min="0"
                    value={form.loyalty_redeem_value}
                    onChange={(e) => setForm({ ...form, loyalty_redeem_value: parseFloat(e.target.value) || 0 })}
                    className="h-10 w-full px-3 border border-velta-border rounded-md text-sm font-mono focus:border-velta-ink focus:outline-none"
                  />
                  <div className="text-[11px] text-velta-mute mt-1">€ scontati ogni &quot;soglia&quot; di punti</div>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-velta-border">
                <input
                  data-testid="brand-loy-auto"
                  type="checkbox"
                  checked={form.loyalty_auto_redeem}
                  onChange={(e) => setForm({ ...form, loyalty_auto_redeem: e.target.checked })}
                  className="w-4 h-4"
                />
                <div>
                  <div className="text-sm font-medium">Suggerimento automatico a cassa</div>
                  <div className="text-[11px] text-velta-mute">Se attivo, al pagamento la cassa propone di scalare i punti del cliente con conferma dell&apos;operatore.</div>
                </div>
              </label>
              <div className="bg-velta-bg rounded-md p-3 text-xs text-velta-mute">
                Esempio: con {form.loyalty_points_per_euro} pt/€, {form.loyalty_redeem_threshold} pt ={" "}
                <span className="font-mono font-bold text-velta-ink">{form.loyalty_redeem_value}€</span> di sconto.
                Un cliente che spende 10€ guadagna <span className="font-mono font-bold text-velta-ink">{Math.round(10 * (form.loyalty_points_per_euro || 0))} pt</span>.
              </div>
            </div>
          </div>

          {/* Preconto Gestionale — PIN segreto */}
          <div className="bg-white border-2 border-dashed border-velta-warning/40 rounded-md p-6">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-4 h-4 text-velta-warning" />
              <h2 className="font-heading font-semibold">Preconto Gestionale — PIN riservato</h2>
            </div>
            <p className="text-xs text-velta-mute mb-4">
              Impostando un PIN qui, chi lo conosce potrà chiudere un tavolo/ordine come <strong>movimento gestionale</strong>: il conto viene registrato ma <strong>non entra nella chiusura fiscale</strong>. Utile per omaggi, prove cucina, giri interni. Il gesto è nascosto: <strong>tieni premuto 2,5 sec sul TOTALE</strong> nella cassa per accedere. Non condividerlo con lo staff.
            </p>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">PIN (4–8 cifre)</label>
            <input
              data-testid="brand-management-pin"
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={8}
              value={form.management_pin}
              onChange={(e) => setForm({ ...form, management_pin: e.target.value.replace(/\D/g, "").slice(0, 8) })}
              placeholder="es. 748291"
              className="h-11 w-40 px-3 border border-velta-border rounded-md font-mono text-lg tracking-[0.3em] focus:border-velta-ink focus:outline-none"
              autoComplete="off"
            />
            <div className="text-[11px] text-velta-mute mt-2">
              {form.management_pin ? "PIN impostato — memorizzalo bene. Cancellalo per disattivare la funzione." : "Nessun PIN: la funzione è disattivata."}
            </div>
          </div>

          {/* Stampa Comanda — evidenzia note in cucina */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-2">
              <Printer className="w-4 h-4 text-[#2F9E77]" />
              <h2 className="font-heading font-semibold">Stampa Comanda — evidenzia le note</h2>
            </div>
            <p className="text-xs text-velta-mute mb-4">
              Come vengono stampate le note articolo (ben cotto, senza cipolla…) sulla stampante cucina/bar.
              Note in evidenza = zero errori in cucina.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="flex items-center gap-2 p-3 border border-velta-border rounded-md cursor-pointer hover:border-velta-ink" data-testid="brand-comanda-bold-wrap">
                <input
                  data-testid="brand-comanda-bold"
                  type="checkbox"
                  checked={!!form.comanda_config?.notes_bold}
                  onChange={(e) => setForm({ ...form, comanda_config: { ...form.comanda_config, notes_bold: e.target.checked } })}
                />
                <span className="text-sm font-medium">Note in <strong>grassetto</strong></span>
              </label>
              <label className="flex items-center gap-2 p-3 border border-velta-border rounded-md cursor-pointer hover:border-velta-ink" data-testid="brand-comanda-upper-wrap">
                <input
                  data-testid="brand-comanda-upper"
                  type="checkbox"
                  checked={!!form.comanda_config?.notes_uppercase}
                  onChange={(e) => setForm({ ...form, comanda_config: { ...form.comanda_config, notes_uppercase: e.target.checked } })}
                />
                <span className="text-sm font-medium">Note in <span style={{ textTransform: "uppercase" }}>MAIUSCOLO</span></span>
              </label>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Dimensione note</label>
                <select
                  data-testid="brand-comanda-size"
                  value={form.comanda_config?.notes_size || "normal"}
                  onChange={(e) => setForm({ ...form, comanda_config: { ...form.comanda_config, notes_size: e.target.value } })}
                  className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
                >
                  <option value="normal">Normale</option>
                  <option value="large">Grande (doppia altezza)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Preconto — intestazione + footer */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-velta-ink" />
              <h2 className="font-heading font-semibold">Stampa Preconto — Intestazione & Piè di pagina</h2>
            </div>
            <p className="text-xs text-velta-mute mb-4">
              Personalizza come si presenta il preconto ai clienti.
              Puoi mettere il nome del locale, un saluto, un QR/link al sito.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Intestazione preconto</label>
                <textarea
                  data-testid="brand-preconto-header"
                  rows={2}
                  value={form.preconto_header}
                  onChange={(e) => setForm({ ...form, preconto_header: e.target.value })}
                  placeholder="Es. TRATTORIA DA MARIO — Preconto tavolo"
                  className="w-full bg-white border border-velta-border rounded-md p-3 outline-none focus:border-[#1E2D5B] resize-none"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Piè di pagina preconto</label>
                <textarea
                  data-testid="brand-preconto-footer"
                  rows={2}
                  value={form.preconto_footer}
                  onChange={(e) => setForm({ ...form, preconto_footer: e.target.value })}
                  placeholder="Es. Grazie e arrivederci · @tuolocale · www.trattoriadamario.it"
                  className="w-full bg-white border border-velta-border rounded-md p-3 outline-none focus:border-[#1E2D5B] resize-none"
                  maxLength={200}
                />
              </div>
            </div>
          </div>

          {/* Note rapide personalizzate — chip in cassa */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <div className="flex items-center gap-2 mb-2">
              <StickyNote className="w-4 h-4 text-velta-ink" />
              <h2 className="font-heading font-semibold">Note rapide personalizzate (chip in cassa)</h2>
            </div>
            <p className="text-xs text-velta-mute mb-4">
              Configura i chip che compaiono in cassa cliccando la nota su una riga.
              Puoi impostare un <strong>prezzo delta</strong> per ogni nota:
              positivo (extra pagante · es. <em>bacon +2€</em>), negativo (sconto · es. <em>senza mozzarella -1€</em>), o 0 (solo indicazione).
            </p>

            <div className="space-y-2">
              {(Array.isArray(form.line_note_suggestions) ? form.line_note_suggestions : []).map((s, i) => (
                <div key={i} data-testid={`brand-chip-row-${i}`} className="flex items-center gap-2 border border-velta-border rounded-md p-2">
                  <input
                    data-testid={`brand-chip-label-${i}`}
                    value={s.label}
                    onChange={(e) => {
                      const arr = [...form.line_note_suggestions];
                      arr[i] = { ...arr[i], label: e.target.value };
                      setForm({ ...form, line_note_suggestions: arr });
                    }}
                    placeholder="Es. senza mozzarella"
                    className="flex-1 h-10 px-3 border border-velta-border rounded-md text-sm outline-none focus:border-[#1E2D5B]"
                    maxLength={40}
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-mono text-velta-mute">€</span>
                    <input
                      data-testid={`brand-chip-price-${i}`}
                      type="number"
                      step="0.5"
                      value={s.price_delta ?? 0}
                      onChange={(e) => {
                        const arr = [...form.line_note_suggestions];
                        arr[i] = { ...arr[i], price_delta: parseFloat(e.target.value) || 0 };
                        setForm({ ...form, line_note_suggestions: arr });
                      }}
                      className="h-10 w-24 px-2 border border-velta-border rounded-md font-mono text-sm text-right outline-none focus:border-[#1E2D5B]"
                    />
                  </div>
                  <button
                    data-testid={`brand-chip-del-${i}`}
                    onClick={() => {
                      const arr = (Array.isArray(form.line_note_suggestions) ? form.line_note_suggestions : []).filter((_, idx) => idx !== i);
                      setForm({ ...form, line_note_suggestions: arr });
                    }}
                    className="h-10 w-10 rounded-md text-velta-error hover:bg-red-50 flex items-center justify-center"
                    title="Rimuovi"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                data-testid="brand-chip-add"
                onClick={() => setForm({ ...form, line_note_suggestions: [...(form.line_note_suggestions || []), { label: "", price_delta: 0 }] })}
                className="h-10 w-full border-2 border-dashed border-velta-border rounded-md text-sm text-velta-mute hover:border-velta-ink hover:text-velta-ink flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Aggiungi chip nota
              </button>
              {(form.line_note_suggestions || []).length === 0 && (
                <p className="text-[11px] text-velta-mute text-center pt-2">
                  Nessun chip personalizzato — verranno mostrati i 6 default (ben cotto, senza cipolla…). Aggiungi almeno un chip qui per sostituirli.
                </p>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-velta-border">
              <span className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Suggerimenti veloci:</span>
              {[
                { label: "senza mozzarella", price_delta: -1 },
                { label: "extra bacon", price_delta: 2 },
                { label: "senza glutine", price_delta: 0 },
                { label: "cottura al sangue", price_delta: 0 },
                { label: "extra formaggio", price_delta: 1.5 },
              ].map((s, i) => (
                <button
                  key={i}
                  onClick={() => setForm({ ...form, line_note_suggestions: [...(form.line_note_suggestions || []), s] })}
                  className="text-[11px] px-2 h-7 bg-velta-bg border border-velta-border rounded-full hover:border-velta-ink"
                >
                  + {s.label} {s.price_delta ? `(${s.price_delta > 0 ? "+" : ""}${s.price_delta}€)` : ""}
                </button>
              ))}
            </div>
          </div>

          {/* Notifiche & Reminder appuntamenti — Toelettatura, Parrucchiere, ecc. */}
          <div className="bg-white border border-velta-border rounded-md p-6" data-testid="brand-notifications-card">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-4 h-4 text-velta-ink" />
              <h2 className="font-heading font-semibold">Notifiche & Reminder appuntamenti</h2>
            </div>
            <p className="text-xs text-velta-mute mb-4">
              Invia reminder automatici via WhatsApp o SMS ai clienti prima dell&apos;appuntamento (toelettatura, parrucchiere, ecc.).
              Scegli il provider e personalizza il testo del messaggio con i placeholder disponibili.
            </p>

            {/* Provider selector */}
            <div className="mb-4">
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Provider di invio</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "wa_deeplink", label: "WhatsApp Manuale (gratis)", desc: "Genera link wa.me, tocchi Invia sul telefono" },
                  { id: "meta_cloud", label: "Meta WhatsApp Cloud API", desc: "Invio automatico, richiede account business" },
                  { id: "twilio_sms", label: "Twilio SMS", desc: "SMS a pagamento tramite Twilio" },
                  { id: "twilio_whatsapp", label: "Twilio WhatsApp", desc: "WhatsApp via Twilio Business" },
                ].map((p) => (
                  <button
                    key={p.id}
                    data-testid={`brand-notif-provider-${p.id}`}
                    onClick={() => setForm({ ...form, notification_config: { ...form.notification_config, provider: p.id } })}
                    className={`text-left border-2 rounded-md p-3 transition ${
                      form.notification_config.provider === p.id
                        ? "border-velta-ink bg-velta-bg"
                        : "border-velta-border hover:border-velta-ink/40"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span className="text-sm font-semibold">{p.label}</span>
                    </div>
                    <p className="text-[11px] text-velta-mute leading-tight">{p.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Meta credentials */}
            {form.notification_config.provider === "meta_cloud" && (
              <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-velta-bg rounded-md" data-testid="brand-notif-meta-fields">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Meta Access Token</label>
                  <input
                    data-testid="brand-notif-meta-token"
                    type="password"
                    value={form.notification_config.meta_access_token}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, meta_access_token: e.target.value } })}
                    placeholder="EAAxxxxxxxxxxxxxx"
                    className="w-full h-10 px-3 border border-velta-border rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Phone Number ID</label>
                  <input
                    data-testid="brand-notif-meta-phoneid"
                    value={form.notification_config.meta_phone_number_id}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, meta_phone_number_id: e.target.value } })}
                    placeholder="1234567890"
                    className="w-full h-10 px-3 border border-velta-border rounded-md text-sm font-mono"
                  />
                </div>
                <p className="col-span-2 text-[11px] text-velta-mute">
                  Ottieni le credenziali su <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="underline text-velta-ink">developers.facebook.com</a> → WhatsApp Business Cloud API.
                </p>
              </div>
            )}

            {/* Twilio credentials */}
            {(form.notification_config.provider === "twilio_sms" || form.notification_config.provider === "twilio_whatsapp") && (
              <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-velta-bg rounded-md" data-testid="brand-notif-twilio-fields">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Account SID</label>
                  <input
                    data-testid="brand-notif-twilio-sid"
                    value={form.notification_config.twilio_sid}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, twilio_sid: e.target.value } })}
                    placeholder="ACxxxxxxxxxxxx"
                    className="w-full h-10 px-3 border border-velta-border rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Auth Token</label>
                  <input
                    data-testid="brand-notif-twilio-token"
                    type="password"
                    value={form.notification_config.twilio_token}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, twilio_token: e.target.value } })}
                    placeholder="••••••••"
                    className="w-full h-10 px-3 border border-velta-border rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Numero mittente</label>
                  <input
                    data-testid="brand-notif-twilio-from"
                    value={form.notification_config.twilio_from}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, twilio_from: e.target.value } })}
                    placeholder="+390123456789"
                    className="w-full h-10 px-3 border border-velta-border rounded-md text-sm font-mono"
                  />
                </div>
                <p className="col-span-3 text-[11px] text-velta-mute">
                  Credenziali disponibili nella <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="underline text-velta-ink">Console Twilio</a>. Per WhatsApp serve un numero approvato Twilio Business.
                </p>
              </div>
            )}

            {/* Template messaggio */}
            <div className="mb-4">
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Template messaggio</label>
              <textarea
                data-testid="brand-notif-template"
                rows={3}
                value={form.notification_config.reminder_template}
                onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, reminder_template: e.target.value } })}
                placeholder="Ciao {owner_name}! Ti ricordiamo l'appuntamento di {pet_name}..."
                className="w-full bg-white border border-velta-border rounded-md p-3 outline-none focus:border-[#1E2D5B] resize-none text-sm"
                maxLength={500}
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Placeholder:</span>
                {["{pet_name}", "{pet_breed}", "{owner_name}", "{date}", "{time}", "{duration}", "{business_name}", "{store_name}", "{store_address}", "{venue}"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setForm({ ...form, notification_config: { ...form.notification_config, reminder_template: form.notification_config.reminder_template + " " + p } })}
                    className="text-[10px] font-mono px-1.5 py-0.5 bg-velta-bg border border-velta-border rounded hover:border-velta-ink"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Lead time & auto-send */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Anticipo reminder</label>
                <select
                  data-testid="brand-notif-hours"
                  value={form.notification_config.hours_before}
                  onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, hours_before: parseInt(e.target.value) } })}
                  className="w-full h-10 px-3 border border-velta-border rounded-md text-sm bg-white"
                >
                  <option value={2}>2 ore prima</option>
                  <option value={4}>4 ore prima</option>
                  <option value={12}>12 ore prima</option>
                  <option value={24}>24 ore prima (1 giorno)</option>
                  <option value={48}>48 ore prima (2 giorni)</option>
                  <option value={72}>72 ore prima (3 giorni)</option>
                  <option value={168}>1 settimana prima</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Invio automatico</label>
                <label className="flex items-center gap-2 h-10 px-3 border border-velta-border rounded-md text-sm bg-white cursor-pointer">
                  <input
                    data-testid="brand-notif-autosend"
                    type="checkbox"
                    checked={form.notification_config.auto_send}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, auto_send: e.target.checked } })}
                  />
                  <span className="text-velta-mute">Attiva scheduler automatico</span>
                </label>
              </div>
            </div>
          </div>

          {/* Sconto Compleanno Pet */}
          <div className="bg-white border border-velta-border rounded-md p-6" data-testid="brand-birthday-card">
            <div className="flex items-center gap-2 mb-2">
              <Cake className="w-4 h-4 text-[#EC4899]" />
              <h2 className="font-heading font-semibold">Sconto Compleanno Pet</h2>
            </div>
            <p className="text-xs text-velta-mute mb-4">
              Applica uno sconto automatico durante il mese di compleanno dell&apos;animale e invia un messaggio di auguri al proprietario. Il flag può essere abilitato per singolo pet dalla scheda appuntamento.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Percentuale sconto</label>
                <div className="relative">
                  <input
                    data-testid="brand-birthday-pct"
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={form.notification_config.birthday_discount_pct}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, birthday_discount_pct: parseFloat(e.target.value) || 0 } })}
                    className="w-full h-10 pl-3 pr-8 border border-velta-border rounded-md text-sm font-mono"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-velta-mute text-sm">%</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Attivazione default</label>
                <label className="flex items-center gap-2 h-10 px-3 border border-velta-border rounded-md text-sm bg-white cursor-pointer">
                  <input
                    data-testid="brand-birthday-default-enabled"
                    type="checkbox"
                    checked={form.notification_config.birthday_default_enabled}
                    onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, birthday_default_enabled: e.target.checked } })}
                  />
                  <span className="text-velta-mute">Attivo per tutti i nuovi pet</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Messaggio compleanno</label>
              <textarea
                data-testid="brand-birthday-template"
                rows={3}
                value={form.notification_config.birthday_message_template}
                onChange={(e) => setForm({ ...form, notification_config: { ...form.notification_config, birthday_message_template: e.target.value } })}
                className="w-full bg-white border border-velta-border rounded-md p-3 outline-none focus:border-[#1E2D5B] resize-none text-sm"
                maxLength={500}
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Placeholder:</span>
                {["{pet_name}", "{pet_breed}", "{owner_name}", "{discount_pct}", "{business_name}"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setForm({ ...form, notification_config: { ...form.notification_config, birthday_message_template: form.notification_config.birthday_message_template + " " + p } })}
                    className="text-[10px] font-mono px-1.5 py-0.5 bg-velta-bg border border-velta-border rounded hover:border-velta-ink"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white border border-velta-border rounded-md p-6">
            <h2 className="font-heading font-semibold mb-3">Messaggio in fondo allo scontrino</h2>
            <textarea
              data-testid="brand-footer"
              value={form.receipt_footer}
              onChange={(e) => setForm({ ...form, receipt_footer: e.target.value })}
              rows="3"
              maxLength="200"
              placeholder="es: Grazie e a presto! Seguici su Instagram @nostropos"
              className="w-full px-3 py-2 border border-velta-border rounded-md font-mono text-xs"
            />
            <div className="text-xs text-velta-mute mt-1 text-right">{form.receipt_footer.length}/200</div>
          </div>

          <div className="flex gap-3">
            <button
              data-testid="brand-preview-btn"
              onClick={() => setShowPreview(true)}
              className="flex-1 h-12 border border-velta-border rounded-md font-medium hover:border-velta-ink"
            >
              Anteprima scontrino
            </button>
            <button
              data-testid="brand-save-btn"
              onClick={save}
              disabled={saving}
              className="flex-1 h-12 bg-velta-ink text-white rounded-md font-medium btn-tactile disabled:opacity-40 inline-flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? "Salvataggio…" : "Salva branding"}
            </button>
          </div>
        </div>

        {/* COL 2: live preview */}
        <div className="lg:sticky lg:top-8 lg:self-start">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-3">Anteprima live</div>
          <div className="bg-velta-bg border border-dashed border-velta-border rounded-md p-5 font-mono text-xs max-w-sm mx-auto" data-testid="brand-live-preview">
            <div className="text-center mb-3">
              {logoPreview && <img src={logoPreview} alt="" className="mx-auto mb-2 max-h-16 object-contain" />}
              <div className="font-bold text-sm">{form.business_name || "RAGIONE SOCIALE"}</div>
              <div className="text-velta-mute">{form.address || "—"}</div>
              <div className="text-velta-mute">P.IVA {form.vat_number || "—"}</div>
              {form.phone && <div className="text-velta-mute">Tel. {form.phone}</div>}
            </div>
            <div className="border-t border-dashed border-velta-border my-2"></div>
            <div className="flex justify-between mb-1"><span>Scontrino N.</span><span className="font-bold">#0042</span></div>
            <div className="flex justify-between mb-3"><span>Data</span><span>{new Date().toLocaleString("it-IT")}</span></div>
            <div className="border-t border-dashed border-velta-border my-2"></div>
            <div className="flex justify-between mb-1"><span>2x Caffè espresso</span><span>2,40 €</span></div>
            <div className="flex justify-between mb-1"><span>3x Cornetto vuoto</span><span>4,50 €</span></div>
            <div className="border-t border-dashed border-velta-border my-2"></div>
            <div className="flex justify-between font-bold text-base"><span>TOTALE</span><span>6,90 €</span></div>
            <div className="border-t border-dashed border-velta-border my-3"></div>
            <div className="text-center text-velta-mute text-[10px]">
              DOCUMENTO COMMERCIALE DI VENDITA
              {form.receipt_footer && (<><br /><span className="block mt-1 whitespace-pre-line">{form.receipt_footer}</span></>)}
              <br />Powered by VELTA SISTEMI
            </div>
          </div>
        </div>
      </div>

      {showPreview && <ScontrinoPreview order={demoOrder} tenant={previewTenant} fiscal onClose={() => setShowPreview(false)} />}
    </div>
  );
}

const Field = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">{label}</label>
    <input
      data-testid={testid}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-3 border border-velta-border rounded-md text-sm focus:border-velta-ink focus:outline-none"
    />
  </div>
);
